import { createContext, useCallback, useContext, useEffect, useState } from "react";

export const translations = {
  en: {
    nav: { about: "About", products: "Products", quality: "Export & Quality", news: "News", contact: "Contact", requestSample: "Request a Sample" },
    hero: {
      eyebrow: "PT Tan Putra Tama · Bekasi, Indonesia",
      line1: "Crafting", line2: "Cocoa", line3: "Excellence",
      subheadline: "Cocoa powders engineered for consistent colour, flavour, and functionality — trusted by manufacturers who value consistency.",
      requestSample: "Request a Sample", viewRange: "View Product Range",
    },
    marquee: ["Consistent Batch-to-Batch", "Natural & Alkalized", "Wide pH Spectrum", "Colour Engineered", "Reliable Supply", "Full Spec Transparency"],
    why: {
      eyebrow: "Why Manufacturers Choose Clara",
      headingA: "Precision is a ", headingEm: "promise", headingB: ", not a claim.",
      intro: "Suitable for bakery, beverages, confectionery, dairy and more — Clara cocoa powders are built for manufacturers who cannot afford surprises.",
      chapters: [
        { title: "Consistent batch-to-batch performance", body: "Every shipment matches the last — engineered so your formulations never drift." },
        { title: "Natural & alkalized portfolio", body: "A complete range of natural and Dutched cocoa powders across pH 5.0 to 11.0." },
        { title: "Wide colour & pH profiles", body: "Nine calibrated grades from light tan to deep black — colour and pH on demand." },
        { title: "Reliable supply & responsive service", body: "Dependable volumes and a team that answers procurement, fast." },
      ],
    },
    about: {
      eyebrow: "About Us · PT Tan Putra Tama",
      heading: "Engineered consistency, at industrial scale.",
      p1: "Founded in 1976, PT Tan Putra Tama has grown into one of Indonesia's most trusted baking-ingredient manufacturers. In 2008 we began producing cocoa powder. Clara-branded cocoa powder is produced at our facility in Cikiwul, Bantargebang, Bekasi, Jawa Barat, Indonesia. We are a manufacturer — not a trader — supplying distributors and importers who value engineered consistency across a wide range of food applications.",
      p2: "Our specialised machinery lets us produce cocoa powder across many grades, colours, and pH profiles — a production flexibility that sets us apart from commodity suppliers. Colour and pH are engineered to your specification, not left to chance.",
      stats: ["Cocoa Grades", "pH Range", "Fineness <75μm"],
    },
    products: {
      eyebrow: "The Full Spectrum",
      headingA: "Nine grades.", headingEm: "Light tan to black.",
      intro: "Buyers shop by colour and pH first. Filter the range, inspect full lab specs, and request the exact grade you need.",
      colourLabel: "Colour", phLabel: "pH Range",
      viewSpecs: "VIEW SPECS",
      noMatch: "No grades match this filter combination.",
      physicalTitle: "Physical Characteristics", microTitle: "Microbiological",
      note: "*Can be customized on request",
      requestSample: "Request Sample", specSheet: "Spec Sheet",
      colours: { All: "All", "Light Brown": "Light Brown", Brown: "Brown", "Dark Brown": "Dark Brown", Black: "Black" },
      specLabels: {
        Color: "Color", pH: "pH", Flavor: "Flavor", "Fat Content": "Fat Content", Fineness: "Fineness",
        Moisture: "Moisture", "Shell Content": "Shell Content", "Total Plate Count": "Total Plate Count",
        Yeast: "Yeast", Mold: "Mold", Enterobacteriaceae: "Enterobacteriaceae", Salmonella: "Salmonella",
        "E. Coli": "E. Coli", Coliform: "Coliform",
      },
      values: { "Up to Standard": "Up to Standard", Negative: "Negative" },
      typeLabel: "Process", appLabel: "Application",
      types: { All: "All", Natural: "Natural", Alkalized: "Alkalized" },
      appLabels: { All: "All", Bakery: "Bakery", Beverages: "Beverages", Confectionery: "Confectionery", Dairy: "Dairy", "Ice Cream": "Ice Cream", "Chocolate Coatings": "Chocolate Coatings" },
      viewSpecsCta: "View specs & download TDS",
    },
    gradePg: {
      backToRange: "All grades",
      bestFor: "Best for",
      downloadTds: "Download TDS (PDF)",
      similar: "Similar grades",
      packLabel: "Packaging", packValue: "25 kg, 3-ply kraft paper bag",
      shelfLabel: "Shelf life", shelfValue: "2 years",
      moqLabel: "Minimum order", moqValue: "Flexible — less than one container (LCL) welcome",
      sampleCta: "Request a sample of",
      prefill: (code) => `I'd like to request a sample of ${code}.`,
      seoTitle: (p, colour) => `${p.code} — ${p.type} ${colour} Cocoa Powder | Clara`,
      seoDesc: (p, colour) => `Clara ${p.code} is a ${p.type.toLowerCase()} cocoa powder — ${colour}, pH ${p.ph} — manufactured by PT Tan Putra Tama in Indonesia. Full specs, TDS download, 25 kg bags, flexible MOQ.`,
    },
    finder: {
      eyebrow: "Grade Finder",
      title: "Not sure which grade? Find yours in 3 steps.",
      cta: "Start the Grade Finder",
      step: (n) => `Step ${n} of 3`,
      q1: "What's your application?",
      q2: "Pick your target colour",
      q3: "Any process preference?",
      noPref: "No preference",
      back: "Back",
      restart: "Start over",
      resultTitle: "Your recommended grade",
      resultReason: "Best fit for your application, colour and process preference.",
      resultFallback: "Closest match — this colour isn't available in that process, so we relaxed the process preference.",
      viewGrade: "View grade",
      requestSample: "Request sample",
    },
    compare: {
      select: "Compare",
      selected: "selected",
      upTo3: "Select up to 3 grades to compare",
      open: "Compare now",
      clear: "Clear",
      title: "Side-by-side comparison",
      diffNote: "Highlighted cells differ between the selected grades.",
      downloadPdf: "Download comparison (PDF)",
    },
    spectrum: {
      eyebrow: "Colour Spectrum",
      title: "Light tan to black — one slide away.",
      viewGrade: "View grade",
    },
    numbers: { eyebrow: "Clara in Numbers", items: [["2008", "Producing since"], ["500 MT/month", "Capacity"], ["9", "Cocoa grades"], ["5.0 – 11.0", "pH range"]] },
    catalogueCta: "Download catalogue (PDF)",
    exportBlock: {
      eyebrow: "Export & Logistics",
      title: "Ready for your market.",
      body: "500 MT/month capacity, 25 kg 3-ply kraft paper bags, flexible minimum orders — less-than-container loads welcome. EXW or FOB Tanjung Priok, with COA, Halal, Certificate of Origin and Health certificate documentation.",
      cta: "See export & logistics",
    },
    exportPg: {
      eyebrow: "Export & Logistics",
      heading: "From Bekasi to your port.",
      intro: "Clara cocoa powders ship worldwide from Tanjung Priok, Jakarta, with export documentation prepared for every order.",
      packLabel: "Packaging", packValue: "25 kg, 3-ply kraft paper bag",
      capacityLabel: "Capacity", capacityValue: "500 MT / month",
      shelfLabel: "Shelf life", shelfValue: "2 years",
      moqLabel: "Minimum order", moqValue: "Flexible — less than one container (LCL) welcome",
      portLabel: "Loading port", portValue: "Tanjung Priok, Jakarta",
      incLabel: "Price terms", incValue: "EXW (Ex Works, TPT facility) · FOB Tanjung Priok",
      leadLabel: "Lead time", leadValue: "3–4 weeks after order confirmation",
      payLabel: "Payment terms", payValue: "L/C (Letter of Credit)",
      marketsLabel: "Markets", marketsValue: "Worldwide — all markets served",
      docsLabel: "Export documents",
      docs: ["Certificate of Analysis (COA)", "Halal certificate", "Certificate of Origin", "Health certificate"],
      cta: "Request a quotation",
      seoTitle: "Export & Logistics — Cocoa Powder from Indonesia | Clara",
      seoDesc: "Clara cocoa powders export from Tanjung Priok, Jakarta in 25 kg 3-ply kraft paper bags with 500 MT/month capacity, flexible MOQ (LCL welcome), EXW or FOB terms and full export documents.",
    },
    events: {
      eyebrow: "Meet Us At",
      title: "Upcoming exhibitions.",
      bookMeeting: "Book a meeting",
      pastLabel: "Past exhibitions",
      boothLabel: "Booth",
      prefill: (name) => `I'd like to book a meeting with Clara at ${name}.`,
    },
    applications: {
      eyebrow: "Applications · Industries Served",
      headingA: "One cocoa portfolio,", headingEm: "every format.",
      items: [
        { name: "Bakery", desc: "Rich colour and stable flavour for cakes, breads and pastries." },
        { name: "Beverages", desc: "Smooth dispersion for chocolate drinks and instant mixes." },
        { name: "Confectionery", desc: "Depth of colour and controlled pH for coatings and fillings." },
        { name: "Dairy", desc: "Consistent performance across flavoured milk and yoghurt." },
        { name: "Ice Cream", desc: "Bold shade and clean melt for frozen desserts." },
        { name: "Chocolate Coatings", desc: "Deep black and dark brown grades for compound coatings." },
      ],
    },
    quality: {
      eyebrow: "Quality & Certifications",
      pageHeading: "Export & Quality",
      headingA: "Certified to be ", headingEm: "trusted.",
      intro: "Procurement teams shortlist on credentials. Clara carries the certifications international buyers require — with full spec transparency behind every grade.",
      items: [
        { name: "Halal Indonesia", desc: "Certified compliant with Indonesian halal standards — essential for export to halal-conscious markets worldwide." },
        { name: "ISO 9001:2015", desc: "Internationally recognised quality management system, ensuring consistent processes and traceable, repeatable quality." },
        { name: "ISO 22000", desc: "International Food Safety Management System standard — specifying the requirements to control food safety hazards along the entire supply chain." },
        { name: "SNI Indonesia", desc: "Conforms to the Indonesian National Standard, verifying product safety and specification integrity." },
        { name: "BPOM", desc: "Registered with Indonesia's National Agency of Drug and Food Control (Badan POM) — verifying product safety and compliance for the Indonesian market." },
      ],
    },
    contact: {
      eyebrow: "Contact · Request a Sample",
      headingA: "Tell us what", headingB: "you're ", headingEm: "making.",
      intro: "Delivering dependable cocoa solutions through consistent quality, reliable supply, and long-term partnerships. Share your requirement and we'll match the grade.",
      labels: { name: "Name", email: "Email", phone: "Phone Number", message: "Inquiry" },
      placeholders: { name: "Your full name", email: "you@company.com", phone: "+62 …", message: "Tell us about the grade, volume and application you need…" },
      submit: "Send Inquiry", sending: "Sending…",
      toastSuccess: "Inquiry sent. Our team will be in touch shortly.",
      toastRequired: "Please enter your name and email.",
      toastFail: "Something went wrong. Please email marketing@tanputratama.co.id.",
    },
    footer: {
      tagline: "Crafting Cocoa Excellence. Dependable cocoa solutions through consistent quality, reliable supply, and long-term partnerships.",
      contact: "Contact", explore: "Explore",
      links: { about: "About Us", products: "Products", quality: "Export & Quality", news: "News" },
      rights: "All rights reserved.",
      certs: "Halal Indonesia · ISO 9001:2015 · ISO 22000 · SNI Indonesia · BPOM",
    },
    seo: {
      home: {
        title: "Clara Cocoa Powder — Natural & Alkalized, Indonesia",
        description: "Clara by PT Tan Putra Tama manufactures natural & alkalized cocoa powders in Bekasi, Indonesia — nine grades from light tan to black, pH 5.0–11.0, for importers worldwide.",
      },
      about: {
        title: "About PT Tan Putra Tama — Indonesian Cocoa Manufacturer",
        description: "PT Tan Putra Tama is a cocoa powder manufacturer in Bekasi, Indonesia — not a trader. Engineered colour and pH across nine Clara grades for global food manufacturers.",
      },
      grades: {
        title: "Cocoa Powder Grades — Natural & Alkalized | Clara",
        description: "Explore nine Clara cocoa powder grades from light tan to deep black. Filter by colour and pH, view full lab specifications and request natural & alkalized samples.",
      },
      applications: {
        title: "Cocoa Applications — Bakery to Beverages | Clara",
        description: "Clara cocoa powders perform across bakery, beverages, confectionery, dairy, ice cream and chocolate coatings — consistent colour, flavour and functionality.",
      },
      quality: {
        title: "Export & Quality — Certifications & Logistics | Clara",
        description: "Clara ships worldwide from Tanjung Priok, Jakarta — 500 MT/month, 25 kg 3-ply kraft bags, flexible MOQ, EXW/FOB, L/C — with Halal, ISO 9001, ISO 22000, SNI and BPOM certifications.",
      },
      contact: {
        title: "Request a Sample or Quotation | Clara Cocoa Powder",
        description: "Tell us what you're making and we'll match the grade. Request cocoa powder samples or a quotation from PT Tan Putra Tama — Bekasi, Indonesia.",
      },
      news: {
        title: "News & Exhibitions | Clara Cocoa Powder",
        description: "Meet Clara at upcoming trade shows — TEI Trade Expo Indonesia and KRISTA Interfood 2026 — plus past exhibitions like Fi Asia Indonesia. Book a meeting with our team.",
      },
    },
  },

  id: {
    nav: { about: "Tentang", products: "Produk", quality: "Ekspor & Mutu", news: "Berita", contact: "Kontak", requestSample: "Minta Sampel" },
    hero: {
      eyebrow: "PT Tan Putra Tama · Bekasi, Indonesia",
      line1: "Menghadirkan", line2: "Kakao", line3: "Terbaik",
      subheadline: "Bubuk kakao yang direkayasa untuk warna, rasa, dan fungsi yang konsisten — dipercaya oleh produsen yang mengutamakan konsistensi.",
      requestSample: "Minta Sampel", viewRange: "Lihat Rangkaian Produk",
    },
    marquee: ["Konsisten Antar-Batch", "Natural & Alkalized", "Spektrum pH Luas", "Warna Terekayasa", "Pasokan Andal", "Transparansi Spesifikasi"],
    why: {
      eyebrow: "Mengapa Produsen Memilih Clara",
      headingA: "Presisi adalah ", headingEm: "janji", headingB: ", bukan sekadar klaim.",
      intro: "Cocok untuk bakery, minuman, kembang gula, produk susu, dan lainnya — bubuk kakao Clara dibuat untuk produsen yang tak bisa menoleransi kejutan.",
      chapters: [
        { title: "Kinerja konsisten antar-batch", body: "Setiap pengiriman sama dengan sebelumnya — direkayasa agar formulasi Anda tak pernah bergeser." },
        { title: "Portofolio natural & alkalized", body: "Rangkaian lengkap bubuk kakao natural dan Dutched pada pH 5,0 hingga 11,0." },
        { title: "Profil warna & pH yang luas", body: "Sembilan grade terkalibrasi dari cokelat muda hingga hitam pekat — warna dan pH sesuai permintaan." },
        { title: "Pasokan andal & layanan responsif", body: "Volume yang dapat diandalkan dan tim yang menjawab kebutuhan pengadaan, dengan cepat." },
      ],
    },
    about: {
      eyebrow: "Tentang Kami · PT Tan Putra Tama",
      heading: "Konsistensi terekayasa, dalam skala industri.",
      p1: "Berdiri sejak 1976, PT Tan Putra Tama tumbuh menjadi salah satu produsen bahan kue paling tepercaya di Indonesia. Pada 2008 kami mulai memproduksi bubuk kakao. Bubuk kakao merek Clara diproduksi di fasilitas kami di Cikiwul, Bantargebang, Bekasi, Jawa Barat, Indonesia. Kami adalah produsen — bukan pedagang — yang memasok distributor dan importir yang mengutamakan konsistensi terekayasa untuk beragam aplikasi pangan.",
      p2: "Mesin khusus kami memungkinkan produksi bubuk kakao dalam beragam grade, warna, dan profil pH — fleksibilitas produksi yang membedakan kami dari pemasok komoditas. Warna dan pH direkayasa sesuai spesifikasi Anda, bukan secara kebetulan.",
      stats: ["Grade Kakao", "Rentang pH", "Kelembutan <75μm"],
    },
    products: {
      eyebrow: "Spektrum Lengkap",
      headingA: "Sembilan grade.", headingEm: "Cokelat muda hingga hitam.",
      intro: "Pembeli memilih berdasarkan warna dan pH lebih dulu. Saring rangkaian, periksa spesifikasi lab lengkap, dan minta grade yang tepat sesuai kebutuhan Anda.",
      colourLabel: "Warna", phLabel: "Rentang pH",
      viewSpecs: "LIHAT SPEK",
      noMatch: "Tidak ada grade yang cocok dengan kombinasi filter ini.",
      physicalTitle: "Karakteristik Fisik", microTitle: "Mikrobiologi",
      note: "*Dapat disesuaikan atas permintaan",
      requestSample: "Minta Sampel", specSheet: "Lembar Spesifikasi",
      colours: { All: "Semua", "Light Brown": "Cokelat Muda", Brown: "Cokelat", "Dark Brown": "Cokelat Tua", Black: "Hitam" },
      specLabels: {
        Color: "Warna", pH: "pH", Flavor: "Rasa", "Fat Content": "Kadar Lemak", Fineness: "Kelembutan",
        Moisture: "Kadar Air", "Shell Content": "Kandungan Kulit", "Total Plate Count": "Angka Lempeng Total",
        Yeast: "Khamir", Mold: "Kapang", Enterobacteriaceae: "Enterobacteriaceae", Salmonella: "Salmonella",
        "E. Coli": "E. Coli", Coliform: "Coliform",
      },
      values: { "Up to Standard": "Sesuai Standar", Negative: "Negatif" },
      typeLabel: "Proses", appLabel: "Aplikasi",
      types: { All: "Semua", Natural: "Natural", Alkalized: "Alkalized" },
      appLabels: { All: "Semua", Bakery: "Bakery", Beverages: "Minuman", Confectionery: "Kembang Gula", Dairy: "Produk Susu", "Ice Cream": "Es Krim", "Chocolate Coatings": "Lapisan Cokelat" },
      viewSpecsCta: "Lihat spek & unduh TDS",
    },
    gradePg: {
      backToRange: "Semua grade",
      bestFor: "Ideal untuk",
      downloadTds: "Unduh TDS (PDF)",
      similar: "Grade serupa",
      packLabel: "Kemasan", packValue: "Kantong kertas kraft 3 lapis, 25 kg",
      shelfLabel: "Umur simpan", shelfValue: "2 tahun",
      moqLabel: "Pesanan minimum", moqValue: "Fleksibel — kurang dari satu kontainer (LCL) diterima",
      sampleCta: "Minta Sampel",
      prefill: (code) => `Saya ingin meminta sampel ${code}.`,
      seoTitle: (p, colour) => `${p.code} — Bubuk Kakao ${p.type} ${colour} | Clara`,
      seoDesc: (p, colour) => `Clara ${p.code} adalah bubuk kakao ${p.type.toLowerCase()} — ${colour.toLowerCase()}, pH ${p.ph} — diproduksi oleh PT Tan Putra Tama di Indonesia. Spesifikasi lengkap, unduh TDS, kemasan 25 kg, MOQ fleksibel.`,
    },
    finder: {
      eyebrow: "Pencari Grade",
      title: "Tidak yakin grade mana? Temukan dalam 3 langkah.",
      cta: "Mulai Pencari Grade",
      step: (n) => `Langkah ${n} dari 3`,
      q1: "Apa aplikasi Anda?",
      q2: "Pilih warna target",
      q3: "Ada preferensi proses?",
      noPref: "Tanpa preferensi",
      back: "Kembali",
      restart: "Ulangi",
      resultTitle: "Grade rekomendasi Anda",
      resultReason: "Paling cocok untuk aplikasi, warna, dan preferensi proses Anda.",
      resultFallback: "Kecocokan terdekat — warna ini tidak tersedia dalam proses tersebut, jadi preferensi proses dikesampingkan.",
      viewGrade: "Lihat grade",
      requestSample: "Minta sampel",
    },
    compare: {
      select: "Bandingkan",
      selected: "dipilih",
      upTo3: "Pilih hingga 3 grade untuk dibandingkan",
      open: "Bandingkan sekarang",
      clear: "Hapus",
      title: "Perbandingan berdampingan",
      diffNote: "Sel yang disorot berbeda di antara grade yang dipilih.",
      downloadPdf: "Unduh perbandingan (PDF)",
    },
    spectrum: {
      eyebrow: "Spektrum Warna",
      title: "Cokelat muda hingga hitam — cukup geser.",
      viewGrade: "Lihat grade",
    },
    numbers: { eyebrow: "Clara dalam Angka", items: [["2008", "Berproduksi sejak"], ["500 MT/bulan", "Kapasitas"], ["9", "Grade kakao"], ["5,0 – 11,0", "Rentang pH"]] },
    catalogueCta: "Unduh katalog (PDF)",
    exportBlock: {
      eyebrow: "Ekspor & Logistik",
      title: "Siap untuk pasar Anda.",
      body: "Kapasitas 500 MT/bulan, kantong kertas kraft 3 lapis 25 kg, pesanan minimum fleksibel — muatan kurang dari satu kontainer (LCL) diterima. EXW atau FOB Tanjung Priok, dengan dokumentasi COA, sertifikat Halal, Certificate of Origin, dan Health certificate.",
      cta: "Lihat ekspor & logistik",
    },
    exportPg: {
      eyebrow: "Ekspor & Logistik",
      heading: "Dari Bekasi ke pelabuhan Anda.",
      intro: "Bubuk kakao Clara dikirim ke seluruh dunia dari Tanjung Priok, Jakarta, dengan dokumentasi ekspor yang disiapkan untuk setiap pesanan.",
      packLabel: "Kemasan", packValue: "Kantong kertas kraft 3 lapis, 25 kg",
      capacityLabel: "Kapasitas", capacityValue: "500 MT / bulan",
      shelfLabel: "Umur simpan", shelfValue: "2 tahun",
      moqLabel: "Pesanan minimum", moqValue: "Fleksibel — kurang dari satu kontainer (LCL) diterima",
      portLabel: "Pelabuhan muat", portValue: "Tanjung Priok, Jakarta",
      incLabel: "Syarat harga", incValue: "EXW (Ex Works, fasilitas TPT) · FOB Tanjung Priok",
      leadLabel: "Waktu tunggu", leadValue: "3–4 minggu setelah konfirmasi pesanan",
      payLabel: "Syarat pembayaran", payValue: "L/C (Letter of Credit)",
      marketsLabel: "Pasar", marketsValue: "Seluruh dunia — semua pasar dilayani",
      docsLabel: "Dokumen ekspor",
      docs: ["Certificate of Analysis (COA)", "Sertifikat Halal", "Certificate of Origin", "Health certificate"],
      cta: "Minta penawaran",
      seoTitle: "Ekspor & Logistik — Bubuk Kakao dari Indonesia | Clara",
      seoDesc: "Bubuk kakao Clara diekspor dari Tanjung Priok, Jakarta dalam kantong kertas kraft 3 lapis 25 kg dengan kapasitas 500 MT/bulan, MOQ fleksibel (LCL diterima), syarat EXW atau FOB, dan dokumen ekspor lengkap.",
    },
    events: {
      eyebrow: "Temui Kami Di",
      title: "Pameran mendatang.",
      bookMeeting: "Jadwalkan pertemuan",
      pastLabel: "Pameran sebelumnya",
      boothLabel: "Booth",
      prefill: (name) => `Saya ingin menjadwalkan pertemuan dengan Clara di ${name}.`,
    },
    applications: {
      eyebrow: "Aplikasi · Industri yang Dilayani",
      headingA: "Satu portofolio kakao,", headingEm: "untuk setiap format.",
      items: [
        { name: "Bakery", desc: "Warna kaya dan rasa stabil untuk kue, roti, dan pastri." },
        { name: "Minuman", desc: "Dispersi halus untuk minuman cokelat dan campuran instan." },
        { name: "Kembang Gula", desc: "Kedalaman warna dan pH terkendali untuk lapisan dan isian." },
        { name: "Produk Susu", desc: "Kinerja konsisten untuk susu rasa dan yoghurt." },
        { name: "Es Krim", desc: "Warna berani dan lelehan bersih untuk makanan penutup beku." },
        { name: "Lapisan Cokelat", desc: "Grade hitam dan cokelat tua pekat untuk lapisan compound." },
      ],
    },
    quality: {
      eyebrow: "Mutu & Sertifikasi",
      pageHeading: "Ekspor & Mutu",
      headingA: "Tersertifikasi untuk ", headingEm: "dipercaya.",
      intro: "Tim pengadaan menyaring berdasarkan kredensial. Clara memiliki sertifikasi yang dibutuhkan pembeli internasional — dengan transparansi spesifikasi penuh di balik setiap grade.",
      items: [
        { name: "Halal Indonesia", desc: "Tersertifikasi sesuai standar halal Indonesia — penting untuk ekspor ke pasar yang mengutamakan kehalalan di seluruh dunia." },
        { name: "ISO 9001:2015", desc: "Sistem manajemen mutu yang diakui internasional, memastikan proses yang konsisten serta mutu yang tertelusur dan berulang." },
        { name: "ISO 22000", desc: "Standar internasional Sistem Manajemen Keamanan Pangan — menetapkan persyaratan untuk mengendalikan bahaya keamanan pangan di sepanjang rantai pasok." },
        { name: "SNI Indonesia", desc: "Sesuai Standar Nasional Indonesia, memverifikasi keamanan produk dan integritas spesifikasi." },
        { name: "BPOM", desc: "Terdaftar di Badan Pengawas Obat dan Makanan (Badan POM) — memverifikasi keamanan produk dan kepatuhan untuk pasar Indonesia." },
      ],
    },
    contact: {
      eyebrow: "Kontak · Minta Sampel",
      headingA: "Beri tahu kami apa", headingB: "yang ", headingEm: "Anda buat.",
      intro: "Menghadirkan solusi kakao yang andal melalui mutu yang konsisten, pasokan yang tepercaya, dan kemitraan jangka panjang. Sampaikan kebutuhan Anda dan kami akan mencocokkan grade-nya.",
      labels: { name: "Nama", email: "Email", phone: "Nomor Telepon", message: "Pesan" },
      placeholders: { name: "Nama lengkap Anda", email: "anda@perusahaan.com", phone: "+62 …", message: "Ceritakan grade, volume, dan aplikasi yang Anda butuhkan…" },
      submit: "Kirim Pesan", sending: "Mengirim…",
      toastSuccess: "Pesan terkirim. Tim kami akan segera menghubungi Anda.",
      toastRequired: "Mohon masukkan nama dan email Anda.",
      toastFail: "Terjadi kesalahan. Silakan email ke marketing@tanputratama.co.id.",
    },
    footer: {
      tagline: "Menghadirkan Kakao Terbaik. Solusi kakao yang andal melalui mutu konsisten, pasokan tepercaya, dan kemitraan jangka panjang.",
      contact: "Kontak", explore: "Jelajahi",
      links: { about: "Tentang Kami", products: "Produk", quality: "Ekspor & Mutu", news: "Berita" },
      rights: "Hak cipta dilindungi.",
      certs: "Halal Indonesia · ISO 9001:2015 · ISO 22000 · SNI Indonesia · BPOM",
    },
    seo: {
      home: {
        title: "Clara Cocoa Powder — Kakao Natural & Alkalized, Indonesia",
        description: "Clara oleh PT Tan Putra Tama memproduksi bubuk kakao natural & alkalized di Bekasi, Indonesia — sembilan grade dari cokelat muda hingga hitam, pH 5,0–11,0, untuk importir dunia.",
      },
      about: {
        title: "Tentang PT Tan Putra Tama — Produsen Kakao Indonesia",
        description: "PT Tan Putra Tama adalah produsen bubuk kakao di Bekasi, Indonesia — bukan pedagang. Warna dan pH terekayasa di sembilan grade Clara untuk produsen pangan global.",
      },
      grades: {
        title: "Grade Bubuk Kakao — Natural & Alkalized | Clara",
        description: "Jelajahi sembilan grade bubuk kakao Clara dari cokelat muda hingga hitam pekat. Saring berdasarkan warna dan pH, lihat spesifikasi lab lengkap, dan minta sampel.",
      },
      applications: {
        title: "Aplikasi Kakao — Bakery hingga Minuman | Clara",
        description: "Bubuk kakao Clara berkinerja di bakery, minuman, kembang gula, produk susu, es krim, dan lapisan cokelat — warna, rasa, dan fungsi konsisten di setiap format.",
      },
      quality: {
        title: "Ekspor & Mutu — Sertifikasi & Logistik | Clara",
        description: "Clara mengirim ke seluruh dunia dari Tanjung Priok, Jakarta — 500 MT/bulan, kantong kraft 3 lapis 25 kg, MOQ fleksibel, EXW/FOB, L/C — dengan sertifikasi Halal, ISO 9001, ISO 22000, SNI, dan BPOM.",
      },
      contact: {
        title: "Minta Sampel atau Penawaran | Clara Cocoa Powder",
        description: "Beri tahu kami apa yang Anda buat dan kami akan mencocokkan grade-nya. Minta sampel bubuk kakao atau penawaran dari PT Tan Putra Tama — Bekasi, Indonesia.",
      },
      news: {
        title: "Berita & Pameran | Clara Cocoa Powder",
        description: "Temui Clara di pameran dagang mendatang — TEI Trade Expo Indonesia dan KRISTA Interfood 2026 — serta pameran sebelumnya seperti Fi Asia Indonesia. Jadwalkan pertemuan dengan tim kami.",
      },
    },
  },

  zh: {
    nav: { about: "关于我们", products: "产品", quality: "出口与质量", news: "新闻", contact: "联系我们", requestSample: "申请样品" },
    hero: {
      eyebrow: "PT Tan Putra Tama · 印度尼西亚贝卡西",
      line1: "精研", line2: "可可", line3: "卓越",
      subheadline: "专为稳定色泽、风味与功能而精研的可可粉 — 深受注重一致性的制造商信赖。",
      requestSample: "申请样品", viewRange: "查看产品系列",
    },
    marquee: ["批次稳定如一", "天然与碱化工艺", "宽广 pH 范围", "色泽精准定制", "供应稳定可靠", "规格完全透明"],
    why: {
      eyebrow: "为何制造商选择 Clara",
      headingA: "精准是", headingEm: "承诺", headingB: "，而非口号。",
      intro: "适用于烘焙、饮料、糖果、乳制品等多种领域 — Clara 可可粉专为无法承受任何意外的制造商而打造。",
      chapters: [
        { title: "批次间性能稳定一致", body: "每一批出货都与上一批完全一致 — 精心打造，确保您的配方永不偏移。" },
        { title: "天然与碱化全系列", body: "覆盖 pH 5.0 至 11.0 的完整天然与碱化（Dutched）可可粉系列。" },
        { title: "宽广的色泽与 pH 规格", body: "九个校准等级，从浅棕到深黑 — 色泽与 pH 按需定制。" },
        { title: "供应可靠，服务高效", body: "稳定的供货量，以及快速响应采购需求的团队。" },
      ],
    },
    about: {
      eyebrow: "关于我们 · PT Tan Putra Tama",
      heading: "工业化规模下的精准一致性。",
      p1: "PT Tan Putra Tama 成立于 1976 年，已发展成为印度尼西亚最值得信赖的烘焙原料制造商之一。2008 年，我们开始生产可可粉。Clara 品牌可可粉产自我们位于印度尼西亚西爪哇省贝卡西 Bantargebang 区 Cikiwul 的工厂。我们是制造商，而非贸易商 — 为注重广泛食品应用领域精准一致性的经销商与进口商供货。",
      p2: "我们的专业设备可生产多种等级、色泽和 pH 规格的可可粉 — 这种生产灵活性使我们区别于大宗商品供应商。色泽和 pH 值均按您的规格定制，而非听天由命。",
      stats: ["可可粉等级", "pH 范围", "细度 <75μm"],
    },
    products: {
      eyebrow: "完整光谱",
      headingA: "九个等级。", headingEm: "浅棕至深黑。",
      intro: "买家首先按色泽和 pH 选购。筛选产品系列，查看完整实验室规格，申请您需要的确切等级。",
      colourLabel: "色泽", phLabel: "pH 范围",
      viewSpecs: "查看规格",
      noMatch: "没有符合此筛选组合的等级。",
      physicalTitle: "物理特性", microTitle: "微生物指标",
      note: "*可按要求定制",
      requestSample: "申请样品", specSheet: "规格表",
      colours: { All: "全部", "Light Brown": "浅棕色", Brown: "棕色", "Dark Brown": "深棕色", Black: "黑色" },
      specLabels: {
        Color: "色泽", pH: "pH", Flavor: "风味", "Fat Content": "脂肪含量", Fineness: "细度",
        Moisture: "水分", "Shell Content": "壳含量", "Total Plate Count": "菌落总数",
        Yeast: "酵母菌", Mold: "霉菌", Enterobacteriaceae: "肠杆菌科", Salmonella: "沙门氏菌",
        "E. Coli": "大肠杆菌", Coliform: "大肠菌群",
      },
      values: { "Up to Standard": "符合标准", Negative: "阴性" },
      typeLabel: "工艺", appLabel: "应用领域",
      types: { All: "全部", Natural: "天然", Alkalized: "碱化" },
      appLabels: { All: "全部", Bakery: "烘焙", Beverages: "饮料", Confectionery: "糖果", Dairy: "乳制品", "Ice Cream": "冰淇淋", "Chocolate Coatings": "巧克力涂层" },
      viewSpecsCta: "查看规格并下载 TDS",
    },
    gradePg: {
      backToRange: "全部等级",
      bestFor: "适用领域",
      downloadTds: "下载 TDS (PDF)",
      similar: "相近等级",
      packLabel: "包装", packValue: "25 公斤三层牛皮纸袋",
      shelfLabel: "保质期", shelfValue: "2 年",
      moqLabel: "最小起订量", moqValue: "灵活 — 欢迎拼箱（LCL）订单",
      sampleCta: "申请样品",
      prefill: (code) => `我想申请 ${code} 的样品。`,
      seoTitle: (p, colour) => `${p.code} — ${p.type === "Natural" ? "天然" : "碱化"}${colour}可可粉 | Clara`,
      seoDesc: (p, colour) => `Clara ${p.code} 是${p.type === "Natural" ? "天然" : "碱化"}可可粉 — ${colour}，pH ${p.ph} — 由印度尼西亚 PT Tan Putra Tama 生产。完整规格、TDS 下载、25 公斤包装、最小起订量灵活。`,
    },
    finder: {
      eyebrow: "等级查找器",
      title: "不确定选哪个等级？三步为您找到。",
      cta: "开始等级查找",
      step: (n) => `第 ${n} 步，共 3 步`,
      q1: "您的应用领域是？",
      q2: "选择目标色泽",
      q3: "有工艺偏好吗？",
      noPref: "无偏好",
      back: "返回",
      restart: "重新开始",
      resultTitle: "为您推荐的等级",
      resultReason: "最符合您的应用领域、色泽和工艺偏好。",
      resultFallback: "最接近的匹配 — 该色泽没有此工艺的产品，因此放宽了工艺偏好。",
      viewGrade: "查看等级",
      requestSample: "申请样品",
    },
    compare: {
      select: "对比",
      selected: "已选",
      upTo3: "最多选择 3 个等级进行对比",
      open: "立即对比",
      clear: "清除",
      title: "并排对比",
      diffNote: "高亮单元格表示所选等级之间存在差异。",
      downloadPdf: "下载对比 (PDF)",
    },
    spectrum: {
      eyebrow: "色泽光谱",
      title: "从浅棕到深黑 — 滑动即见。",
      viewGrade: "查看等级",
    },
    numbers: { eyebrow: "Clara 数据一览", items: [["2008", "投产年份"], ["500 吨/月", "产能"], ["9", "可可粉等级"], ["5.0 – 11.0", "pH 范围"]] },
    catalogueCta: "下载产品目录 (PDF)",
    exportBlock: {
      eyebrow: "出口与物流",
      title: "随时发往您的市场。",
      body: "月产能 500 吨，25 公斤三层牛皮纸袋包装，最小起订量灵活 — 欢迎拼箱（LCL）订单。EXW 或 FOB 丹戎不碌（雅加达）条款，提供 COA、清真证书、原产地证书和健康证书等出口文件。",
      cta: "查看出口与物流",
    },
    exportPg: {
      eyebrow: "出口与物流",
      heading: "从贝卡西直达您的港口。",
      intro: "Clara 可可粉从雅加达丹戎不碌港发往全球，每笔订单均备妥出口文件。",
      packLabel: "包装", packValue: "25 公斤三层牛皮纸袋",
      capacityLabel: "产能", capacityValue: "500 吨/月",
      shelfLabel: "保质期", shelfValue: "2 年",
      moqLabel: "最小起订量", moqValue: "灵活 — 欢迎拼箱（LCL）订单",
      portLabel: "装运港", portValue: "雅加达丹戎不碌港",
      incLabel: "价格条款", incValue: "EXW（工厂交货）· FOB 丹戎不碌",
      leadLabel: "交货周期", leadValue: "订单确认后 3–4 周",
      payLabel: "付款方式", payValue: "L/C（信用证）",
      marketsLabel: "市场", marketsValue: "全球 — 服务所有市场",
      docsLabel: "出口文件",
      docs: ["分析证书 (COA)", "清真证书", "原产地证书", "健康证书"],
      cta: "索取报价",
      seoTitle: "出口与物流 — 印度尼西亚可可粉 | Clara",
      seoDesc: "Clara 可可粉从雅加达丹戎不碌港出口，25 公斤三层牛皮纸袋，月产能 500 吨，最小起订量灵活（欢迎拼箱），EXW 或 FOB 条款，出口文件齐全。",
    },
    events: {
      eyebrow: "展会相约",
      title: "即将到来的展会。",
      bookMeeting: "预约会面",
      pastLabel: "往届展会",
      boothLabel: "展位",
      prefill: (name) => `我想预约与 Clara 在 ${name} 的会面。`,
    },
    applications: {
      eyebrow: "应用领域 · 服务行业",
      headingA: "一个可可产品组合，", headingEm: "覆盖所有形态。",
      items: [
        { name: "烘焙", desc: "为蛋糕、面包和糕点提供浓郁色泽与稳定风味。" },
        { name: "饮料", desc: "顺滑分散，适用于巧克力饮品和速溶冲调粉。" },
        { name: "糖果", desc: "深邃色泽与受控 pH，适用于涂层和夹心。" },
        { name: "乳制品", desc: "在调味乳和酸奶中表现始终稳定。" },
        { name: "冰淇淋", desc: "色泽浓郁、融化纯净的冷冻甜点之选。" },
        { name: "巧克力涂层", desc: "深黑与深棕等级，适用于复合涂层。" },
      ],
    },
    quality: {
      eyebrow: "质量与认证",
      pageHeading: "出口与质量",
      headingA: "以认证赢得", headingEm: "信赖。",
      intro: "采购团队以资质筛选供应商。Clara 拥有国际买家所需的各项认证 — 每个等级背后都有完全透明的规格支持。",
      items: [
        { name: "Halal Indonesia", desc: "通过印度尼西亚清真标准认证 — 出口全球清真市场的必备资质。" },
        { name: "ISO 9001:2015", desc: "国际认可的质量管理体系，确保流程一致、质量可追溯且可重复。" },
        { name: "ISO 22000", desc: "国际食品安全管理体系标准 — 规定整个供应链中食品安全危害的控制要求。" },
        { name: "SNI Indonesia", desc: "符合印度尼西亚国家标准，验证产品安全性与规格完整性。" },
        { name: "BPOM", desc: "已在印度尼西亚国家药品和食品监督管理局（Badan POM）注册 — 验证产品在印尼市场的安全性与合规性。" },
      ],
    },
    contact: {
      eyebrow: "联系我们 · 申请样品",
      headingA: "告诉我们您在", headingB: "制作", headingEm: "什么。",
      intro: "以稳定的质量、可靠的供应和长期的合作伙伴关系，提供值得信赖的可可解决方案。告诉我们您的需求，我们将为您匹配等级。",
      labels: { name: "姓名", email: "电子邮箱", phone: "电话号码", message: "咨询内容" },
      placeholders: { name: "您的姓名", email: "you@company.com", phone: "+86 …", message: "请告诉我们您需要的等级、数量和应用领域…" },
      submit: "发送咨询", sending: "发送中…",
      toastSuccess: "咨询已发送。我们的团队将尽快与您联系。",
      toastRequired: "请输入您的姓名和电子邮箱。",
      toastFail: "出现问题。请发送邮件至 marketing@tanputratama.co.id。",
    },
    footer: {
      tagline: "匠心可可，卓越品质。以稳定的质量、可靠的供应和长期的合作伙伴关系，提供值得信赖的可可解决方案。",
      contact: "联系方式", explore: "探索",
      links: { about: "关于我们", products: "产品", quality: "出口与质量", news: "新闻" },
      rights: "版权所有。",
      certs: "Halal Indonesia · ISO 9001:2015 · ISO 22000 · SNI Indonesia · BPOM",
    },
    seo: {
      home: {
        title: "Clara 可可粉 — 印度尼西亚天然与碱化可可粉",
        description: "Clara 由 PT Tan Putra Tama 在印度尼西亚贝卡西生产天然与碱化可可粉 — 九个等级，从浅棕到深黑，pH 5.0–11.0，服务全球进口商。",
      },
      about: {
        title: "关于 PT Tan Putra Tama — 印度尼西亚可可制造商",
        description: "PT Tan Putra Tama 是位于印度尼西亚贝卡西的可可粉制造商，而非贸易商。九个 Clara 等级的色泽与 pH 均精准定制，服务全球食品制造商。",
      },
      grades: {
        title: "可可粉等级 — 天然与碱化 | Clara",
        description: "探索九个 Clara 可可粉等级，从浅棕到深黑。按色泽和 pH 筛选，查看完整实验室规格，申请天然与碱化样品。",
      },
      applications: {
        title: "可可应用 — 从烘焙到饮料 | Clara",
        description: "Clara 可可粉在烘焙、饮料、糖果、乳制品、冰淇淋和巧克力涂层中表现出色 — 色泽、风味和功能始终一致。",
      },
      quality: {
        title: "出口与质量 — 认证与物流 | Clara",
        description: "Clara 从雅加达丹戎不碌港发往全球 — 月产能 500 吨，25 公斤三层牛皮纸袋，起订量灵活，EXW/FOB，信用证付款 — 并持有 Halal、ISO 9001、ISO 22000、SNI 和 BPOM 认证。",
      },
      contact: {
        title: "申请样品或索取报价 | Clara 可可粉",
        description: "告诉我们您在制作什么，我们将为您匹配等级。向 PT Tan Putra Tama（印度尼西亚贝卡西）申请可可粉样品或报价。",
      },
      news: {
        title: "新闻与展会 | Clara 可可粉",
        description: "在即将到来的展会与 Clara 相会 — TEI 印尼贸易博览会和 KRISTA Interfood 2026 — 以及 Fi Asia Indonesia 等往届展会。预约与我们团队会面。",
      },
    },
  },
};

const LangContext = createContext(null);

export const LangProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => localStorage.getItem("clara_lang") || "en");

  useEffect(() => {
    localStorage.setItem("clara_lang", lang);
    document.documentElement.lang = lang === "zh" ? "zh-CN" : lang;
  }, [lang]);

  const setLang = useCallback((l) => setLangState(l), []);

  return (
    <LangContext.Provider value={{ lang, setLang, t: translations[lang] }}>
      {children}
    </LangContext.Provider>
  );
};

// Builds a language-prefixed path, e.g. lp("/grades") → "/en/grades".
export const usePath = () => {
  const { lang } = useLang();
  return useCallback((p = "/") => `/${lang}${p === "/" ? "" : p}`, [lang]);
};

export const useLang = () => {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang must be used within LangProvider");
  return ctx;
};
